import React, { Component } from 'react';
import _AdminService from 'modules/AdminService';
import _AnswerKeyService from 'modules/AnswerKeyService';
import _notification from 'modules/notification';
import { Card, Table, Modal, Tag, Select } from 'antd';
import EmptyState from 'components/enterprise/EmptyState';
import LoadingSpinner from 'components/enterprise/LoadingSpinner';
import PageHeader from 'components/enterprise/PageHeader';
import DropdownList from 'components/controls/DropdownList';
import { routesAdmin } from 'routes/Routes';
import ButtonComponent from 'components/controls/Button';
import { GENERAL_ERROR_MESSAGE } from 'constants/constants';
import moment from 'moment';

const { Option } = Select;

// Phase C.9 (Answer Key & Lightweight Online Objection Workflow) — a lightweight admin review screen
// for candidate-submitted objections. Deliberately NOT a generic ticketing/helpdesk UI: view, filter,
// open detail, view proof, set status, record a resolution note. Nothing more.
const STATUS_OPTIONS = ['Submitted', 'UnderReview', 'Accepted', 'Rejected', 'Resolved'];
const STATUS_TAG_COLOR = {
    Submitted: 'blue', UnderReview: 'orange', Accepted: 'green', Rejected: 'red', Resolved: 'purple'
};

class AnswerKeyObjectionMaster_new extends Component {
    constructor(props) {
        super(props);
        this.state = {
            pagination: { pageSize: 10, current: 1 },
            data: [],
            posts: [],
            loading: false,
            filterPostId: null,
            filterStatus: null,
            showDetailModal: false,
            selected: null,
            decisionStatus: '',
            resolutionNote: '',
            saving: false
        };
    }

    componentDidMount() {
        this.loadPosts();
        this.search();
    }

    loadPosts = async () => {
        try {
            const response = await _AdminService.GetAllPost();
            this.setState({ posts: response.data || [] });
        } catch (e) { /* non-fatal; Post filter just stays empty */ }
    }

    search = async () => {
        this.setState({ loading: true });
        try {
            const response = await _AnswerKeyService.searchObjections(this.state.filterPostId, null, this.state.filterStatus);
            this.setState({ data: response.data || [], loading: false });
        } catch (e) {
            this.setState({ loading: false });
            _notification.showMessage('error', GENERAL_ERROR_MESSAGE);
        }
    }

    // Pagination bug fix (same class found across several admin masters): the Table's pagination
    // prop is bound to state, but without this onChange wired, clicking a page number never
    // actually updated `pagination.current` - the page selector changed visually but the rows didn't.
    onTableAction = (pagination) => {
        this.setState({ pagination });
    }

    onPostFilterChange = (event) => {
        const postId = event.target.value ? parseInt(event.target.value, 10) : null;
        this.setState({ filterPostId: postId }, this.search);
    }

    onStatusFilterChange = (value) => {
        this.setState({ filterStatus: value || null }, this.search);
    }

    openDetail = (record) => {
        this.setState({ showDetailModal: true, selected: record, decisionStatus: record.Status, resolutionNote: record.ResolutionNote || '' });
    }

    closeDetail = () => {
        this.setState({ showDetailModal: false, selected: null, decisionStatus: '', resolutionNote: '' });
    }

    viewProof = async (objectionId) => {
        try {
            const response = await _AnswerKeyService.getObjectionProofAccessUrl(objectionId);
            if (response.status === 200 && response.data && response.data.Url) {
                window.open(response.data.Url, '_blank');
            } else {
                _notification.showMessage('error', 'Could not open proof document.');
            }
        } catch (e) {
            _notification.showMessage('error', 'Could not open proof document.');
        }
    }

    handleSaveDecision = async () => {
        const { selected, decisionStatus, resolutionNote } = this.state;
        if (!decisionStatus) {
            _notification.showMessage('error', 'Please select a status.');
            return;
        }
        this.setState({ saving: true });
        try {
            const response = await _AnswerKeyService.updateObjectionStatus({
                Id: selected.Id,
                Status: decisionStatus,
                ResolutionNote: resolutionNote
            });
            if (response.status === 200) {
                _notification.showMessage('success', 'Objection updated.');
                this.setState({ saving: false });
                this.closeDetail();
                this.search();
            } else {
                this.setState({ saving: false });
                _notification.showMessage('error', (response.data && response.data.Message) || GENERAL_ERROR_MESSAGE);
            }
        } catch (e) {
            this.setState({ saving: false });
            _notification.showMessage('error', GENERAL_ERROR_MESSAGE);
        }
    }

    render() {
        const { selected } = this.state;

        const TableColumns = [
            { title: 'Reference', dataIndex: 'ReferenceNumber', key: 'ReferenceNumber', width: '10%' },
            { title: 'Candidate', dataIndex: 'StudentName', key: 'StudentName', width: '15%' },
            { title: 'Roll No.', dataIndex: 'RollNumber', key: 'RollNumber', width: '8%' },
            { title: 'Post', dataIndex: 'PostName', key: 'PostName', width: '13%' },
            { title: 'Batch', dataIndex: 'ClassRoomName', key: 'ClassRoomName', width: '14%' },
            { title: 'Question', dataIndex: 'QuestionReference', key: 'QuestionReference', width: '12%' },
            {
                title: 'Submitted', dataIndex: 'SubmittedAt', key: 'SubmittedAt', width: '12%',
                render: (value) => value ? moment(value).format('DD-MMM-YYYY hh:mm A') : ''
            },
            {
                title: 'Status', dataIndex: 'Status', key: 'Status', width: '9%',
                render: (value) => <Tag color={STATUS_TAG_COLOR[value]}>{value}</Tag>
            },
            {
                title: 'Action', dataIndex: '', key: 'Id', width: '10%',
                render: (record) => <a onClick={() => this.openDetail(record)}>Review</a>
            },
        ];

        if (this.state.loading && this.state.data.length === 0)
            return <LoadingSpinner />

        return (
            <div className="container-fluid p-3">
                <PageHeader
                    title="Answer Key Objections"
                    breadcrumbItems={[{ label: 'Dashboard', path: routesAdmin.dashboard.path }, { label: 'Answer Key Objections' }]}
                />
                <div className="analysisWrap">
                    <div className="max1200 m-auto mb-2">
                        <div className="row">
                            <div className="col-sm-4 mb-2">
                                <DropdownList
                                    name="filterPostId"
                                    value={this.state.filterPostId || 0}
                                    onChange={this.onPostFilterChange}
                                    placeholder="All Posts"
                                    heading="Filter by Post"
                                    options={this.state.posts}
                                />
                            </div>
                            <div className="col-sm-4 mb-2">
                                <label className="card-holder-name">Filter by Status</label>
                                <Select
                                    allowClear
                                    style={{ width: '100%' }}
                                    placeholder="All Statuses"
                                    value={this.state.filterStatus || undefined}
                                    onChange={this.onStatusFilterChange}
                                >
                                    {STATUS_OPTIONS.map(s => <Option key={s} value={s}>{s}</Option>)}
                                </Select>
                            </div>
                        </div>
                    </div>

                    <div className="max1200 m-auto">
                        <Table size='small'
                            bordered
                            rowKey='Id'
                            columns={TableColumns}
                            dataSource={this.state.data}
                            pagination={this.state.pagination}
                            onChange={this.onTableAction}
                            footer={() => <label style={{ textAlign: 'right', marginRight: '20px' }}>Total Records: {this.state.data.length}</label>}
                            className="table-striped-rows admin-table"
                            locale={{ emptyText: <EmptyState icon="inbox" title="No Objections Found" description="No objections match the current filters." /> }}
                        />
                    </div>
                </div>

                <Modal
                    title="Objection Detail"
                    visible={this.state.showDetailModal}
                    onCancel={this.closeDetail}
                    footer={null}
                    width={560}
                    className="modelPopAnt admin-home"
                    destroyOnClose={true}
                >
                    {selected && (
                        <Card>
                            <p><b>Reference:</b> {selected.ReferenceNumber}</p>
                            <p><b>Candidate:</b> {selected.StudentName} (Roll No. {selected.RollNumber})</p>
                            <p><b>Post / Batch:</b> {selected.PostName} / {selected.ClassRoomName}</p>
                            <p><b>Question:</b> {selected.QuestionReference}</p>
                            <p><b>Objection:</b></p>
                            <p style={{ background: '#f5f7fa', padding: '10px 14px', borderRadius: 4 }}>{selected.ObjectionText}</p>
                            <p><b>Submitted:</b> {moment(selected.SubmittedAt).format('DD-MMM-YYYY hh:mm A')}</p>
                            {selected.HasProof && (
                                <p><a onClick={() => this.viewProof(selected.Id)}>View Submitted Proof</a></p>
                            )}

                            <div className="mb-2 mt-3">
                                <DropdownList
                                    name="decisionStatus"
                                    value={this.state.decisionStatus || ''}
                                    onChange={(e) => this.setState({ decisionStatus: e.target.value })}
                                    placeholder="Select Status"
                                    heading="Status"
                                    isRequired={true}
                                    options={STATUS_OPTIONS.map(s => ({ Id: s, Name: s }))}
                                />
                            </div>
                            <div className="mb-2">
                                <label className="card-holder-name">Resolution Note</label>
                                <textarea
                                    className="form-control"
                                    rows={3}
                                    value={this.state.resolutionNote}
                                    onChange={(e) => this.setState({ resolutionNote: e.target.value })}
                                    placeholder="Optional note visible to the candidate (e.g. reason for the decision)"
                                />
                            </div>
                            <div className="pt-2 buttonAlignCenter">
                                <ButtonComponent type="button" value="Cancel" onClick={this.closeDetail} className="button-global button-secondary mr-4 autoHeight small" />
                                <ButtonComponent type="button" value={this.state.saving ? "Saving…" : "Save Decision"} onClick={this.handleSaveDecision} className="button-global button-primary autoHeight small" disabled={this.state.saving} />
                            </div>
                        </Card>
                    )}
                </Modal>
            </div>
        )
    };
}

export default AnswerKeyObjectionMaster_new;
